import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    Handle,
    Position,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import { toPng } from "html-to-image";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Building2, FolderClosed, BookOpen, Sparkles, ChevronLeft, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * LearningMindMap — a NotebookLM-style expandable mind map of the Learning
 * Hub for one department:
 *     Department (root)
 *       └─ Collections  ⇢ click chevron to expand
 *            └─ Folders + loose resources  ⇢ click chevron to reveal contents
 *
 * Layout is auto-computed with `dagre` (left→right) so nodes never overlap
 * regardless of the tree size. The whole canvas can be exported as PNG.
 */

const BRANCH_COLOURS = [
    "#60a5fa", // blue
    "#34d399", // emerald
    "#fbbf24", // amber
    "#f472b6", // pink
    "#a78bfa", // violet
    "#22d3ee", // sky
    "#f87171", // rose
    "#a3e635", // lime
];

// dagre width/height hints — real widths vary but dagre just needs a ballpark.
const NODE_W = 260;
const NODE_H = 48;

const layoutWithDagre = (nodes, edges) => {
    const g = new dagre.graphlib.Graph({ compound: false });
    g.setDefaultEdgeLabel(() => ({}));
    g.setGraph({ rankdir: "LR", nodesep: 18, ranksep: 110, marginx: 20, marginy: 20 });
    nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
    edges.forEach((e) => g.setEdge(e.source, e.target));
    dagre.layout(g);
    return nodes.map((n) => {
        const pos = g.node(n.id);
        return {
            ...n,
            position: { x: pos.x - NODE_W / 2, y: pos.y - NODE_H / 2 },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
        };
    });
};

// NotebookLM-style dark pill with an optional expand/collapse chevron on the right.
const PillNode = ({ data }) => {
    const isRoot = data.kind === "root";
    const canExpand = data.hasChildren;
    const expanded = data.expanded;
    return (
        <div
            className={`relative flex items-stretch rounded-2xl shadow-[0_8px_24px_-14px_rgba(15,23,42,0.6)] transition-all ${isRoot ? "ring-2 ring-[#2EB4AC]/40" : ""}`}
            style={{ backgroundColor: "#1f2937" }}
            data-testid={`mm-node-${data.kind}-${data.id}`}
        >
            <Handle type="target" position={Position.Left} className="!opacity-0" />
            <button
                type="button"
                onClick={data.onLabelClick}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white max-w-[300px] text-left"
                style={{ color: isRoot ? "#ffffff" : data.color || "#ffffff" }}
            >
                {isRoot && <Building2 className="h-4 w-4 shrink-0" />}
                {data.kind === "folder" && <FolderClosed className="h-3.5 w-3.5 shrink-0" />}
                {data.kind === "resource" && <BookOpen className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{data.label}</span>
                {data.badge != null && (
                    <span
                        className="ml-1 text-[10px] tabular-nums font-bold px-1.5 py-0.5 rounded-full text-slate-900"
                        style={{ backgroundColor: data.color || "#ffffff" }}
                    >
                        {data.badge}
                    </span>
                )}
            </button>
            {canExpand && (
                <button
                    type="button"
                    onClick={data.onToggle}
                    aria-label={expanded ? "Collapse" : "Expand"}
                    data-testid={`mm-toggle-${data.kind}-${data.id}`}
                    className="flex items-center justify-center px-2 border-l border-white/10 hover:bg-white/5 rounded-r-2xl transition"
                >
                    <ChevronLeft
                        className={`h-4 w-4 text-white/70 transition-transform ${expanded ? "rotate-0" : "rotate-180"}`}
                    />
                </button>
            )}
            <Handle type="source" position={Position.Right} className="!opacity-0" />
        </div>
    );
};

const nodeTypes = { pill: PillNode };

const LearningMindMap = () => {
    const { user } = useAuth();
    const { depts } = useData();
    const navigate = useNavigate();
    const [departmentId, setDepartmentId] = useState(user?.department_id || "");
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(new Set()); // node ids that are expanded
    const [exporting, setExporting] = useState(false);
    const canvasRef = useRef(null);

    const dept = useMemo(() => depts?.find((d) => d.id === departmentId) || null, [depts, departmentId]);

    // Fetch collections → folders → resources for the picked department.
    useEffect(() => {
        if (!departmentId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const c = await api.get(`/learning/collections?department_id=${departmentId}`);
                const collections = c.data || [];
                const perColl = await Promise.all(collections.map(async (col) => {
                    const [fRes, rRes] = await Promise.all([
                        api.get(`/learning/collections/${col.id}/folders`).catch(() => ({ data: [] })),
                        api.get(`/learning/collections/${col.id}/resources`).catch(() => ({ data: [] })),
                    ]);
                    const folders = (fRes.data || []).map((f) => ({
                        folder: f,
                        resources: (rRes.data || []).filter((r) => r.folder_id === f.id),
                    }));
                    const loose = (rRes.data || []).filter((r) => !r.folder_id);
                    return { collection: col, folders, loose };
                }));
                if (!cancelled) {
                    setTree(perColl);
                    // Auto-expand: root + every collection by default. Folders stay
                    // collapsed until the user clicks their chevron.
                    const initial = new Set([`root-${departmentId}`]);
                    perColl.forEach((c) => initial.add(`col-${c.collection.id}`));
                    setExpanded(initial);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [departmentId]);

    const toggle = useCallback((id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    // Build nodes/edges from the tree, honouring the expanded-set. Only children
    // of expanded nodes are rendered so users progressively reveal detail.
    const { nodes, edges } = useMemo(() => {
        if (!tree || !dept) return { nodes: [], edges: [] };
        const rawNodes = [];
        const rawEdges = [];
        const rootId = `root-${dept.id}`;
        const rootHasChildren = tree.length > 0;
        rawNodes.push({
            id: rootId,
            type: "pill",
            position: { x: 0, y: 0 },
            data: {
                id: dept.id,
                label: dept.name,
                kind: "root",
                hasChildren: rootHasChildren,
                expanded: expanded.has(rootId),
                onToggle: () => toggle(rootId),
                onLabelClick: () => toggle(rootId),
                color: "#ffffff",
            },
        });
        if (!expanded.has(rootId)) return { nodes: layoutWithDagre(rawNodes, rawEdges), edges: rawEdges };

        tree.forEach((c, idx) => {
            const color = BRANCH_COLOURS[idx % BRANCH_COLOURS.length];
            const cid = `col-${c.collection.id}`;
            const cCount = c.folders.length + c.loose.length;
            rawNodes.push({
                id: cid,
                type: "pill",
                position: { x: 0, y: 0 },
                data: {
                    id: c.collection.id,
                    label: c.collection.name,
                    kind: "collection",
                    color,
                    badge: cCount || null,
                    hasChildren: cCount > 0,
                    expanded: expanded.has(cid),
                    onToggle: () => toggle(cid),
                    onLabelClick: () => navigate(`/learning?open=${c.collection.id}`),
                },
            });
            rawEdges.push({
                id: `e-root-${cid}`,
                source: rootId,
                target: cid,
                type: "smoothstep",
                style: { stroke: color, strokeWidth: 2 },
            });
            if (!expanded.has(cid)) return;

            // Folders under this collection.
            c.folders.forEach((fw) => {
                const fid = `fld-${fw.folder.id}`;
                const fCount = fw.resources.length;
                rawNodes.push({
                    id: fid,
                    type: "pill",
                    position: { x: 0, y: 0 },
                    data: {
                        id: fw.folder.id,
                        label: fw.folder.name,
                        kind: "folder",
                        color,
                        badge: fCount || null,
                        hasChildren: fCount > 0,
                        expanded: expanded.has(fid),
                        onToggle: () => toggle(fid),
                        onLabelClick: () => navigate(`/learning?open=${c.collection.id}`),
                    },
                });
                rawEdges.push({
                    id: `e-${cid}-${fid}`,
                    source: cid,
                    target: fid,
                    type: "smoothstep",
                    style: { stroke: color, strokeWidth: 1.5, opacity: 0.75 },
                });
                if (!expanded.has(fid)) return;
                fw.resources.forEach((r) => {
                    const rid = `res-${r.id}`;
                    rawNodes.push({
                        id: rid,
                        type: "pill",
                        position: { x: 0, y: 0 },
                        data: {
                            id: r.id,
                            label: r.name || "Untitled",
                            kind: "resource",
                            color,
                            hasChildren: false,
                            onLabelClick: () => {
                                if (r.url) window.open(r.url, "_blank", "noopener");
                                else navigate(`/learning?open=${c.collection.id}`);
                            },
                        },
                    });
                    rawEdges.push({
                        id: `e-${fid}-${rid}`,
                        source: fid,
                        target: rid,
                        type: "smoothstep",
                        style: { stroke: color, strokeWidth: 1, opacity: 0.55, strokeDasharray: "4 4" },
                    });
                });
            });

            // Loose resources — attached directly to the collection (no fake
            // "(no folder)" bucket node).
            c.loose.forEach((r) => {
                const rid = `res-${r.id}`;
                rawNodes.push({
                    id: rid,
                    type: "pill",
                    position: { x: 0, y: 0 },
                    data: {
                        id: r.id,
                        label: r.name || "Untitled",
                        kind: "resource",
                        color,
                        hasChildren: false,
                        onLabelClick: () => {
                            if (r.url) window.open(r.url, "_blank", "noopener");
                            else navigate(`/learning?open=${c.collection.id}`);
                        },
                    },
                });
                rawEdges.push({
                    id: `e-${cid}-${rid}`,
                    source: cid,
                    target: rid,
                    type: "smoothstep",
                    style: { stroke: color, strokeWidth: 1, opacity: 0.55, strokeDasharray: "4 4" },
                });
            });
        });
        return { nodes: layoutWithDagre(rawNodes, rawEdges), edges: rawEdges };
    }, [tree, dept, expanded, navigate, toggle]);

    // Snapshot the mind-map as a PNG. Measures every rendered node in FLOW
    // coordinates, then rasterises the whole viewport at natural (1×) scale
    // with generous padding so nothing ever gets cropped, regardless of how
    // wide/tall the graph is.
    const handleDownloadPng = useCallback(async () => {
        if (!canvasRef.current || nodes.length === 0) return;
        setExporting(true);
        try {
            const viewportEl = canvasRef.current.querySelector(".react-flow__viewport");
            const containerEl = canvasRef.current.querySelector(".react-flow__renderer") || viewportEl.parentElement;
            if (!viewportEl || !containerEl) return;

            const cssMatrix = new DOMMatrixReadOnly(window.getComputedStyle(viewportEl).transform);
            const scale = cssMatrix.a || 1;
            const tx = cssMatrix.e || 0;
            const ty = cssMatrix.f || 0;
            const containerRect = containerEl.getBoundingClientRect();

            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            canvasRef.current.querySelectorAll(".react-flow__node").forEach((el) => {
                const r = el.getBoundingClientRect();
                const fx = (r.left - containerRect.left - tx) / scale;
                const fy = (r.top - containerRect.top - ty) / scale;
                const fw = r.width / scale;
                const fh = r.height / scale;
                minX = Math.min(minX, fx);
                minY = Math.min(minY, fy);
                maxX = Math.max(maxX, fx + fw);
                maxY = Math.max(maxY, fy + fh);
            });
            if (!isFinite(minX)) return;

            const pad = 80;
            const width = Math.ceil(maxX - minX + pad * 2);
            const height = Math.ceil(maxY - minY + pad * 2);
            const shiftX = -minX + pad;
            const shiftY = -minY + pad;

            const dataUrl = await toPng(viewportEl, {
                backgroundColor: "#0f172a",
                width,
                height,
                pixelRatio: 2,
                cacheBust: true,
                style: {
                    width: `${width}px`,
                    height: `${height}px`,
                    transform: `translate(${shiftX}px, ${shiftY}px) scale(1)`,
                    transformOrigin: "0 0",
                },
            });
            const a = document.createElement("a");
            a.href = dataUrl;
            a.download = `mindmap-${dept?.name || "department"}.png`;
            a.click();
        } catch (e) {
            console.error("Mind map PNG export failed", e);
        } finally {
            setExporting(false);
        }
    }, [nodes, dept]);

    return (
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-4" data-testid="learning-mindmap">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="min-w-0">
                    <h2 className="font-heading text-xl font-bold text-slate-900 inline-flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-[#2EB4AC]" /> Learning Mind Map
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">Click any node&apos;s chevron to expand. Click a title to jump into the Learning Hub.</p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Department</label>
                    <select
                        value={departmentId}
                        onChange={(e) => setDepartmentId(e.target.value)}
                        data-testid="mindmap-dept-picker"
                        className="h-10 px-3 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none text-sm"
                    >
                        <option value="">— pick a department —</option>
                        {(depts || []).map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={handleDownloadPng}
                        disabled={exporting || nodes.length <= 1}
                        data-testid="mindmap-download-png"
                        className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        <Download className="h-4 w-4" />
                        {exporting ? "Exporting…" : "PNG"}
                    </button>
                </div>
            </div>

            <div
                ref={canvasRef}
                className="h-[600px] rounded-xl overflow-hidden border border-slate-800"
                style={{ backgroundColor: "#0f172a" }}
                data-testid="mindmap-canvas"
            >
                {!departmentId ? (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                        Pick a department to render its mind map.
                    </div>
                ) : loading ? (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">Loading…</div>
                ) : nodes.length <= 1 && (!tree || tree.length === 0) ? (
                    <div className="h-full flex items-center justify-center text-sm text-slate-400">
                        No collections in this department yet.
                    </div>
                ) : (
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.15, minZoom: 0.4, maxZoom: 1.4 }}
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={true}
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background gap={22} size={1} color="#1e293b" />
                        <Controls showInteractive={false} className="!bg-slate-800 !border-slate-700 [&>button]:!bg-slate-800 [&>button]:!border-slate-700 [&>button]:!text-slate-200" />
                        <MiniMap
                            zoomable
                            pannable
                            maskColor="rgba(15,23,42,0.85)"
                            nodeColor={() => "#334155"}
                            nodeStrokeColor="#475569"
                            style={{ backgroundColor: "#1e293b", borderRadius: 8 }}
                        />
                    </ReactFlow>
                )}
            </div>
        </div>
    );
};

export default LearningMindMap;
